import React from 'react';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/utils';

/* Shared schema-driven settings controls.

   Extracted from DeviceSettingsView so the per-Hearth device settings screen
   and the per-Higlo settings section (HigloSettingsSection) render settings
   rows identically. A "setting" here is one entry from a master-settings
   schema doc: { name, category, type, value, label, description, tooltip,
   choices?, range?, step?, order }.

   The Default model: a setting is "on Default" when the user tier holds no
   value for it (userValue === undefined). While on Default the value control
   is shown but disabled, displaying `defaultValue` — the value the device
   will actually use if the user doesn't intervene. Callers that can see the
   admin override tier pass `defaultValue = override ?? schemaGlobal`; when
   the override tier is unreadable (e.g. rules not deployed yet) they fall
   back to the schema global alone. */

export function SettingRow({
  setting,
  userValue,
  defaultValue,
  disabled,
  divider,
  onToggleCustom,
  onChange,
}) {
  const isDefault = userValue === undefined;
  const effectiveDefault = defaultValue !== undefined ? defaultValue : setting.value;
  const displayValue = isDefault ? effectiveDefault : userValue;
  const inputDisabled = isDefault || disabled;
  /* One piece of explanation per row, always as text under the title — never
     a hover tooltip (Seyed, 2026-08-13). Settings whose schema carries only a
     `tooltip` (e.g. the In-Room Portraits category) show that text instead of
     showing nothing. */
  const subtext = setting.description || setting.tooltip;

  return (
    <div className={cn('px-4 py-3', divider && 'border-b border-slate-100')}>
      {/* Top row: title on the left, Default toggle on the right. */}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[14px] font-semibold text-slate-900">
              {setting.label || setting.name}
            </span>
          </div>
          {subtext && (
            <div className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
              {subtext}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
          <span className="text-[11px] text-slate-500">Default</span>
          <Switch
            checked={isDefault}
            onCheckedChange={(checked) => onToggleCustom(!checked)}
            disabled={disabled}
            aria-label={`${setting.label || setting.name} use default`}
          />
        </div>
      </div>
      {/* Below the title: full-width value control. Disabled when Default is
          on (so the user can see what the default is without being able to
          change it) or when the caller disables the whole row. */}
      <div className={cn('mt-2', inputDisabled && 'opacity-60 pointer-events-none')}>
        <SettingInput
          setting={setting}
          value={displayValue}
          onChange={onChange}
          disabled={inputDisabled}
        />
      </div>
    </div>
  );
}

/* ──────────────── Input (schema-driven) ──────────────── */

export function SettingInput({ setting, value, onChange, disabled }) {
  /* Enum-style: any setting that declares a `choices` array renders as a
     native <select>. Values are coerced back to number or boolean when the
     schema defines them as such — labelled bools (e.g. "Version 1"/"Version 2"
     mapping to false/true) must reach Firestore as real booleans or the
     Hearth's type containment rejects the write. */
  if (Array.isArray(setting.choices) && setting.choices.length > 0) {
    return (
      <select
        className="w-full text-[14px] px-3 py-2 border border-slate-200 rounded-md bg-white text-slate-900 disabled:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
        value={String(value)}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          /* Coerce by the schema's declared type, not by sniffing: a string
             setting whose choice value happens to look numeric (e.g. "2")
             or boolean-like (e.g. "true") must stay a string. */
          const numeric =
            setting.type === 'int' || setting.type === 'float' || setting.type === 'number';
          if (numeric) {
            onChange(Number(raw));
          } else if (setting.type === 'bool') {
            onChange(raw === 'true');
          } else {
            onChange(raw);
          }
        }}
      >
        {setting.choices.map((c) => (
          <option key={String(c.value)} value={String(c.value)}>
            {c.label || String(c.value)}
          </option>
        ))}
      </select>
    );
  }

  /* Booleans as a two-option <select> — visually consistent with the rest
     of the settings rows and gives the disabled/default state a natural
     read. */
  if (setting.type === 'bool' || typeof setting.value === 'boolean') {
    return (
      <select
        className="w-full text-[14px] px-3 py-2 border border-slate-200 rounded-md bg-white text-slate-900 disabled:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
        value={value ? 'true' : 'false'}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === 'true')}
      >
        <option value="true">On</option>
        <option value="false">Off</option>
      </select>
    );
  }

  /* Numeric with a range → slider + live readout. */
  const isNumerical =
    setting.type === 'int' || setting.type === 'float' || setting.type === 'number'
    || typeof setting.value === 'number';
  const min = setting.range?.[0];
  const max = setting.range?.[1];
  const step = setting.step ?? 1;
  if (isNumerical && min !== undefined && max !== undefined) {
    return (
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-slate-900 disabled:cursor-not-allowed"
        />
        <span className="text-[13px] font-medium text-slate-800 min-w-[2.5rem] text-right">
          {value}
        </span>
      </div>
    );
  }

  /* Unbounded numeric → bare number input. */
  if (isNumerical) {
    return (
      <input
        type="number"
        value={value}
        disabled={disabled}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full text-[14px] px-3 py-2 border border-slate-200 rounded-md bg-white text-slate-900 disabled:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
      />
    );
  }

  /* Free-form string fallback. */
  return (
    <input
      type="text"
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="w-full text-[14px] px-3 py-2 border border-slate-200 rounded-md bg-white text-slate-900 disabled:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
    />
  );
}

